import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Row, Col, Form, Select, Checkbox, Typography } from 'antd';
import { fetchMakes } from '../../../store/actions/vehicleMakes-actions';
import { fetchModelsByMake } from '../../../store/actions/vehicleModels-actions';
import { fetchGenerationsByModel } from '../../../store/actions/vehicleGenerations-actions';
import { fetchEquipmentsByGeneration } from '../../../store/actions/vehicleEquipments-actions';
import { Status } from '../../../helpers/consts/status';

const { Option } = Select;
const { Text } = Typography;

const VehicleInformationField = ({ form, selectedVehicle }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { status, makes } = useSelector((state) => state.makes);
  const { filteredModels } = useSelector((state) => state.models);
  const { filteredGenerations } = useSelector((state) => state.generations);
  const { filteredEquipments } = useSelector((state) => state.equipments);

  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [selectedEquipments, setSelectedEquipments] = useState([]);

  useEffect(() => {
    if (!makes.length && status !== Status.LOADING) {
      dispatch(fetchMakes());
    }
  }, [dispatch, makes.length, status]);

  useEffect(() => {
    if (selectedVehicle) {
      const {
        vehicleMakeId,
        vehicleModelId,
        vehicleGenerationId,
        vehicleEquipments,
      } = selectedVehicle;

      const vehicleEquipmentsId = vehicleEquipments?.map((equip) => equip.id);

      // Reset the form fields when `selectedVehicle` changes
      form.setFieldsValue({
        vehicleMakeId,
        vehicleModelId,
        vehicleGenerationId,
        vehicleEquipments: vehicleEquipmentsId || [],
      });

      setSelectedMake(vehicleMakeId);
      setSelectedModel(vehicleModelId);
      setSelectedGeneration(vehicleGenerationId);
      setSelectedEquipments(vehicleEquipmentsId);

      if (vehicleMakeId) {
        dispatch(fetchModelsByMake({ vehicleMakeId }));
      }

      if (vehicleModelId) {
        dispatch(fetchGenerationsByModel({ vehicleModelId }));
      }

      if (vehicleGenerationId) {
        dispatch(fetchEquipmentsByGeneration({ vehicleGenerationId }));
      }
    } else {
      resetForm();
    }
  }, [selectedVehicle, dispatch, form]);

  // Use useEffect to call handleEquipmentChange once the filteredEquipments are available
  useEffect(() => {
    if (filteredEquipments.length > 0) {
      const vehicleEquipmentsId = selectedEquipments;
      handleEquipmentChange(vehicleEquipmentsId);
    }
  }, [filteredEquipments, selectedEquipments]);

  const resetForm = () => {
    setSelectedMake(null);
    setSelectedModel(null);
    setSelectedGeneration(null);
    setSelectedEquipments([]);
    form.resetFields([
      'vehicleModelId',
      'vehicleGenerationId',
      'vehicleEquipments',
    ]);
  };

  const handleMakeChange = (makeId) => {
    resetSelection();
    setSelectedMake(makeId);
    dispatch(fetchModelsByMake({ vehicleMakeId: makeId }));
    form.setFieldsValue({
      vehicleEquipments: null,
      vehicleGenerationId: null,
      vehicleModelId: null,
    });
  };

  const handleModelsChange = (modelId) => {
    resetSelection('model');
    setSelectedModel(modelId);
    dispatch(fetchGenerationsByModel({ vehicleModelId: modelId }));
    form.setFieldsValue({ vehicleGenerationId: null, vehicleEquipments: null });
  };

  const handleGenerationChange = (generationId) => {
    setSelectedGeneration(generationId);
    dispatch(
      fetchEquipmentsByGeneration({ vehicleGenerationId: generationId })
    );
    form.setFieldsValue({ vehicleEquipments: null });
  };

  const handleEquipmentChange = (checkedValues) => {
    form.setFieldsValue({ vehicleEquipments: checkedValues });
  };

  const resetSelection = (type) => {
    if (type !== 'model') {
      setSelectedModel(null);
      form.resetFields([
        'vehicleModelId',
        'vehicleGenerationId',
        'vehicleEquipments',
      ]);
    }
    if (type !== 'generation') {
      setSelectedGeneration(null);
      form.resetFields(['vehicleGenerationId', 'vehicleEquipments']);
    }
  };

  return (
    <Row gutter={16}>
      <Col xs={24} sm={12}>
        <Form.Item
          name="vehicleMakeId"
          label={t('form.vehicleForm.vehicleInformationField.makeLabel')}
          rules={[{ required: true, message: t('form.vehicleForm.vehicleInformationField.makeRequired') }]}
        >
          <Select
            placeholder={t('form.vehicleForm.vehicleInformationField.makePlaceholder')}
            showSearch
            allowClear
            onChange={handleMakeChange}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {makes.map((make) => (
              <Option key={make.id} value={make.id}>
                {make.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      <Col xs={24} sm={12}>
        <Form.Item
          name="vehicleModelId"
          label={t('form.vehicleForm.vehicleInformationField.modelLabel')}
          rules={[{ required: true, message: t('form.vehicleForm.vehicleInformationField.modelRequired') }]}
        >
          <Select
            placeholder={t('form.vehicleForm.vehicleInformationField.modelPlaceholder')}
            showSearch
            allowClear
            onChange={handleModelsChange}
            disabled={!selectedMake}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {filteredModels.map((model) => (
              <Option key={model.id} value={model.id}>
                {model.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      <Col xs={24} sm={12}>
        <Form.Item
          name="vehicleGenerationId"
          label={t('form.vehicleForm.vehicleInformationField.generationLabel')}
          rules={[{ required: true, message: t('form.vehicleForm.vehicleInformationField.generationRequired')  }]}
        >
          <Select
            placeholder={t('form.vehicleForm.vehicleInformationField.generationPlaceholder')}
            showSearch
            allowClear
            onChange={handleGenerationChange}
            disabled={!selectedModel}
            filterOption={(input, option) => {
              const yearRange = option.children.toLowerCase();
              return (
                yearRange.includes(input.toLowerCase()) ||
                option.key.toLowerCase().includes(input.toLowerCase())
              );
            }}
          >
            {filteredGenerations.map((generation) => (
              <Option key={generation.id} value={generation.id}>
                {generation.yearBegin} - {generation.yearEnd}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      {selectedGeneration && (
        <Col xs={24} sm={24}>
          <Form.Item name="vehicleEquipments" label={t('form.vehicleForm.vehicleInformationField.equipmentLabel')}>
            {filteredEquipments.length > 0 ? (
              <Checkbox.Group
                defaultValue={selectedEquipments}
                onChange={handleEquipmentChange}
                style={{ width: '100%' }}
              >
                <Row gutter={[16, 16]}>
                  {filteredEquipments.map((equipment) => (
                    <Col xs={12} sm={12} md={4} lg={4} key={equipment.id}>
                      <Checkbox value={equipment.id}>{equipment.name}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            ) : (
              <Text type="secondary">{t('form.vehicleForm.vehicleInformationField.noEquipment')}</Text>
            )}
          </Form.Item>
        </Col>
      )}
    </Row>
  );
};

export default VehicleInformationField;
